import { ReactNode } from 'react';
import { ITextProps } from '../HBText/HBText';
import type {
    HBButtonCustomTheme,
    HBButtonTheme,
    TButtonSize
} from '../HBButton/HBButton.types';
import { IActionTestIdProps } from '../common/internal/test-id.types';
import { IconProps } from '@honeybook/icons';

export enum ToastType {
    CustomInfo = 'custom-info',
    Error = 'error',
    Info = 'info',
    MemberToast = 'member-toast',
    Success = 'success',
    Warning = 'warning',
    ai = 'ai'
}

export interface IToastActionLink
    extends Omit<ITextProps, 'children' | 'type' | 'testId'>,
        IActionTestIdProps {
    onClick(): void;
    text: ReactNode;
    type?: ITextProps['type'];
}

export interface IToastAction extends IActionTestIdProps {
    customTheme?: HBButtonCustomTheme;
    disabled?: boolean;
    loading?: boolean;
    onClick: () => void;
    size?: TButtonSize;
    text: ReactNode;
    theme?: HBButtonTheme;
}

export interface IMemberToastAction extends IActionTestIdProps {
    text: ReactNode;
    onClick: () => void;
}

interface ICommonToastProps {
    /** Renders a styled text link action below the toast content. */
    actionLink?: IToastActionLink;
    /** Button actions rendered at the bottom of the toast. */
    actions?: IToastAction[];
    /** Auto-dismisses the toast after the specified milliseconds; set to false to prevent auto-dismiss. */
    autoDismissMs?: number | false;
    id?: string;
    /** When true, shows a close button allowing the user to manually dismiss the toast. */
    isDismissible?: boolean;
    /** Callback to remove this toast from the toast list by its id. */
    removeItem?: (id: string) => void;
    onDismiss?: () => void;
    text?: ReactNode;
    title?: ReactNode;
    /** Determines the toast variant, controlling icon, color scheme, and rendering behavior. */
    type: ToastType;
}

export interface ISystemToastProps extends ICommonToastProps {
    type:
        | ToastType.CustomInfo
        | ToastType.Error
        | ToastType.Info
        | ToastType.Success
        | ToastType.Warning
        | ToastType.ai;

    /** Custom background color override for CustomInfo toast type. */
    customColor?: string;
    /** Pass a component to render a custom icon, or false to hide the icon entirely; undefined uses the default icon for the toast type. */
    icon?: false | ((props: IconProps) => JSX.Element);
    /** Overrides the default icon color derived from the toast type. */
    iconColor?: string;
    /** Controls content flow direction: 'row' places text beside the icon, 'column' stacks text below. */
    layout?: 'row' | 'column';
}

export interface IMemberToastProps extends ICommonToastProps {
    type: ToastType.MemberToast;
    /** Limited to one or two actions for the member toast layout. */
    actions?: [IMemberToastAction, IMemberToastAction?];
    /** URL for the avatar image displayed as the toast icon. */
    iconUrl: string;
    /** When true, this toast is displayed before non-prioritized member toasts. */
    isPrioritized?: boolean;
}

export type ToastProps = ISystemToastProps | IMemberToastProps;
